import React, { Component, Fragment } from 'react';
import { compose, Query } from '../../../reactApolloCompat';
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';
import gql from 'graphql-tag';
import { Icon, IconButton, InputAdornment } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from '../../../components/DragDrop';
import Grid from '../../../components/layout/Grid';
import Modal from '../../../components/modal/Modal';
import Inline, { inlineAlignment } from '../../../components/layout/Inline';
import TextField from '../../../components/form/TextField';
import Label from '../../../components/form/Label';
import Checkbox from '../../../components/form/Checkbox';
import Button from '../../../components/form/Button';
import FlexGrid from '../../../components/layout/FlexGrid';
import Spinner from '../../../components/loading/Spinner';
import Table, { Cell, Row } from '../../../components/table/Table';
import NotesReadOnly from '../../../components/form/NotesReadOnly';
import createForm from '../../../utils/createForm';
import { diff, isNullOrUndefined, round } from '../../../utils/objects';
import BillTo from '../Billing/BillTo';
import { createInvoiceFunc, submitToXeroFunc, updateInvoiceFunc } from '../Billing/GetSaveInvoice';
import { joinDefined, prettyPrice } from '../../../utils/strings';
import { GST } from '../../../utils/bookable';
import { niceDateTimeFromString, niceTimeFromString } from '../../../utils/date';
import { withSnackbarMessage } from '../../../context/SnackbarMessage';
import { BackIcon, CloseIcon, DeleteIcon, NextIcon, SaveIcon, UsdCircleIcon } from '../../../components/IconIndex';
import ReadonlyField from '../../../components/form/ReadonlyField';
import LineItemAutoComplete from '../../../components/form/LineItemAutoComplete';
import Select from '../../../components/form/Select';

class InvoiceModal extends Component {
    state = {
        tabIndex: 0,
        invoiceIndex: null,
        editingItemIndex: null,
        loading: false,
        form: null,
        gstPrices: [],
        notes: null
    };

    static getDerivedStateFromProps({ open, invoiceID, form, quotes }, state) {
        let invoiceIndex = -1;
        const invoices = form.getField('Invoices');
        if (invoices && invoices.length > 0) {
            invoiceIndex = invoices.findIndex(obj => invoiceID === obj.ID);
        }
        const invoice = form.getField(`Invoices[${invoiceIndex}]`);
        const legacyKey = invoice && invoice.LegacyKey;
        const xeroID = invoice && invoice.XeroID;
        const newState = { open };

        if (state.open !== open && open) {
            newState.tabIndex = 0;
            newState.cart = [];

            if (!isNullOrUndefined(invoice)) {
                let clonedInvoice = cloneDeep(invoice);

                if (!!clonedInvoice.Customer && !clonedInvoice.Customer.BillingSource)
                    clonedInvoice.Customer.BillingSource = !!clonedInvoice.Customer.RelationToDeceased
                        ? 'AuthorisedContact'
                        : 'Other';
                const oldContext = state.form !== null ? state.form.context : null;
                newState.form = createForm(oldContext, clonedInvoice);
                newState.invoiceIndex = invoiceIndex;
                newState.gstPrices = clonedInvoice.InvoiceItems.map(x => getPriceIncGst(x));
                newState.notes = clonedInvoice.Notes;
                delete clonedInvoice.Notes;

                const toggledQuotes = {};
                if (quotes && quotes.length && !clonedInvoice.ID) {
                    quotes.forEach(e => {
                        setInvoiceFromQuotes({ value: e.quoteID, checked: true }, { quotes, invoice });
                        toggledQuotes['toggle' + e.quoteID] = true;
                    });
                }
                newState.toggledQuotes = toggledQuotes;
            } else {
                newState.form = null;
                newState.invoiceIndex = null;
            }

            // newState.defaultBillTo = defaultBillTo;
            newState.canEdit = !legacyKey && !xeroID;
            newState.hasExportedToXero = !!xeroID;
            newState.existing = invoiceIndex !== null;
        }

        return newState;
    }

    componentDidUpdate(_, oldState) {
        const { loading, open, invoiceIndex, form } = this.state;
        if (open && invoiceIndex === null && !loading) {
            //no invoice! create one
            this.createInvoice();
            return;
        }

        if (oldState.form === null && form !== null) form.context = this;
    }

    render() {
        const { open } = this.props;
        const { tabIndex } = this.state;

        const { form } = this.state;
        if (!form) return null;

        const steps = [];

        // if not authorised to xero, allow editing details.
        if (!form.getField('ID') || ['NOT EXPORTED', 'DRAFT', 'SUBMITTED'].indexOf(form.getField('XeroStatus')) > -1) {
            steps.push({
                abbreviation: 'Billing',
                title: 'Step One:',
                subtitle: 'Confirm the billing details',
                onChangeTab: this.onChangeTab,
                content: <div>{this.renderBillingDetails()}</div>,
                actions: this.renderBillingDetailsActions()
            });
            steps.push({
                abbreviation: 'Line Items',
                title: 'Step Two:',
                subtitle: 'Review the invoice dates and line items',
                onChangeTab: this.onChangeTab,
                content: <div>{this.renderInvoiceDetails(true)}</div>,
                actions: this.renderInvoiceDetailsActions(true)
            });
        }

        // if invoice exported show pdf, otherwise show export
        if (
            !form.getField('XeroStatus') ||
            ['NOT EXPORTED'].indexOf(form.getField('XeroStatus')) === 0 ||
            this.invoiceIsModified() === true
        ) {
            steps.push({
                abbreviation: 'Submit',
                title: 'Step Three:',
                subtitle: 'Submit the invoice',
                onChangeTab: this.onChangeTab,
                content: <div>{this.renderInvoiceDetails(false)}</div>,
                actions: this.renderInvoiceDetailsActions(false)
            });
        } else {
            steps.push({
                abbreviation: 'View Invoice',
                title: 'Invoice:',
                subtitle: '#' + form.getField('ID'),
                onChangeTab: this.onChangeTab,
                content: <div>{this.renderInvoiceDetails(false)}</div>,
                actions: this.renderPreviewActions()
            });
            steps.push({
                abbreviation: 'View PDF',
                title: 'Invoice:',
                subtitle: '#' + form.getField('ID'),
                onChangeTab: this.onChangeTab,
                content: <div>{this.renderPreview()}</div>,
                actions: this.renderPreviewActions()
            });
        }
        // if invoice saved, allow notes
        if (!!form.getField('ID')) {
            steps.push({
                abbreviation: 'Notes',
                title: 'Invoice:',
                subtitle: '#' + form.getField('ID'),
                onChangeTab: this.onChangeTab,
                content: this.renderNoteHistory(),
                actions: this.renderNoteHistoryActions()
            });
        }

        return (
            <Modal
                open={open}
                variant="stepped"
                onClose={this.closeModal}
                canClickOut={false}
                activeTab={tabIndex && tabIndex > steps.length ? steps.length : tabIndex}
                steps={steps}
            />
        );
    }

    closeModal = () => {
        const { onClose } = this.props;
        if (onClose) onClose();
        this.setState({
            tabIndex: 0,
            invoiceIndex: null,
            editingItemIndex: null,
            loading: false,
            form: null,
            gstPrices: [],
            notes: null
        });
    };

    onChangeTab = tabIndex => {
        this.setState({ tabIndex });
    };

    onSubmit = () => {
        this.updateInvoice();
    };

    onAddNote = () => {
        this.updateInvoice(true);
    };

    onDelete = () => {
        const objType = this.isPurchaseInvoice() ? 'bill' : 'invoice';
        if (window.confirm(`Are you sure you want to discard this ${objType}?`)) {
            const { form, invoiceIndex } = this.state;
            if (!form) return null;
            this.setState({ loading: true });
            const invoice = { ID: form.state.ID, Archived: true };
            const original = { ID: form.state.ID };
            updateInvoiceFunc(invoice, original).then(({ data }) => {
                if (data && data.updateCMInvoice) {
                    const { form: parentForm } = this.props;
                    const Invoices = parentForm.getField('Invoices');
                    Invoices.splice(invoiceIndex, 1);
                    parentForm.setField({ Invoices }, true);
                    this.closeModal();
                }
            });
        }
    };

    createInvoice() {
        const { type: orderType, form: parentForm } = this.props;
        if (parentForm.loading !== false) return;

        this.setState({ loading: true });
        const that = this;

        let invoiceObj = {
            IsPurchase: this.isPurchaseInvoice(),
            InvoiceItems: [],
            InvoiceDate: moment().format('YYYY-MM-DD')
        };
        if (orderType === 'Plaque') {
            invoiceObj.PlaqueOrder = { ID: parentForm.getField('ID') };
        } else {
            const dos = moment(parentForm.getField('CremationDate'));
            const due = (!!dos && dos.isValid() && dos) || moment();

            invoiceObj.InvoiceDueDate = due.add(21, 'days').format('YYYY-MM-DD');
            invoiceObj.CremationOrder = { ID: parentForm.getField('ID') };
        }

        //add form details
        const form = createForm(that, invoiceObj);
        form.setField({ Customer: this.defaultCustomer() });

        const Invoices = parentForm.getField('Invoices');
        const invoiceIndex = Invoices.length;
        that.setState({ loading: false, invoiceIndex, form });
        return null;
    }

    invoiceIsModified(skipNotes = true) {
        const { invoiceIndex, form } = this.state;

        if (!form) return null;

        let invoice = form.state;
        const { form: parentForm } = this.props;
        const original = parentForm.getField(`Invoices[${invoiceIndex}]`);

        if (!original) return true;

        const trimmedOriginal = cloneDeep(original);
        const trimmedCurrent = cloneDeep(invoice);
        if (skipNotes) {
            delete trimmedOriginal.Notes;
            delete trimmedCurrent.Notes;
        }

        const test = diff(trimmedCurrent, trimmedOriginal, false);
        // onload we fudge the billing source, so ignore it too
        if (
            Object.keys(test).length === 1 &&
            test.Customer &&
            test.Customer.BillingSource === 'Other' &&
            original.Customer &&
            original.Customer.ID &&
            !original.Customer.BillingSource
        )
            delete test.Customer;

        return !!Object.keys(test).length;
    }

    updateInvoice(noteOnly = false) {
        const { invoiceIndex, form: { state: invoice } } = this.state;
        const { form: parentForm } = this.props;

        this.setState({ loading: 'save' });
        const that = this;
        const original = parentForm.getField(`Invoices[${invoiceIndex}]`);

        if (!invoice.ID || invoice.ID === '0')
            return createInvoiceFunc(invoice).then(
                ({ data }) => {
                    const Invoices = parentForm.getField('Invoices');
                    Invoices.push(cloneDeep(data.createCMInvoice));
                    parentForm.setField({ Invoices }, true);

                    const createdInvoice = cloneDeep(data.createCMInvoice);
                    const invoiceIndex = Invoices.length - 1;

                    const notes = createdInvoice.Notes;
                    delete createdInvoice.Notes;
                    that.setState({
                        loading: false,
                        invoiceIndex,
                        form: createForm(that, createdInvoice),
                        editingItemIndex: null,
                        notes: notes
                    });
                },
                e => that.onGqlError('Failed to create invoice.', e)
            );

        if (!invoice.XeroID || !!noteOnly) {
            return updateInvoiceFunc(invoice, original).then(
                ({ data }) => {
                    const Invoices = parentForm.getField('Invoices');
                    let updatedInvoice = cloneDeep(data.updateCMInvoice);

                    const notes = updatedInvoice.Notes;
                    delete updatedInvoice.Notes;
                    Object.assign(Invoices[invoiceIndex], cloneDeep(data.updateCMInvoice));
                    parentForm.setField({ Invoices }, true);
                    that.setState({
                        loading: false,
                        form: createForm(that, updatedInvoice),
                        editingItemIndex: null,
                        notes: notes
                    });
                },
                e => that.onGqlError('Failed to update invoice.', e)
            );
        } else {
            // update case after exported to xero
            return updateInvoiceFunc(invoice, original).then(
                () => {
                    return submitToXeroFunc(invoice).then(
                        result => {
                            const Invoices = parentForm.getField('Invoices');
                            const updatedInvoice = cloneDeep(result.data.submitCMInvoiceToXero);
                            Object.assign(Invoices[invoiceIndex], cloneDeep(result.data.submitCMInvoiceToXero));
                            parentForm.setField({ Invoices }, true);
                            that.setState({
                                loading: false,
                                invoice: updatedInvoice,
                                hasExportedToXero: true,
                                canEdit: false,
                                editingItemIndex: null
                            });
                        },
                        e => that.onGqlError('Failed to submit invoice to xero.', e)
                    );
                },
                e => that.onGqlError('Failed to update invoice.', e)
            );
        }
    }

    submitToXero() {
        const { form: parentForm } = this.props;
        const { invoiceIndex, form } = this.state;

        this.setState({ loading: 'xero' });
        const that = this;
        const original = parentForm.getField(`Invoices[${invoiceIndex}]`);

        const writeFunc = form.getField('ID') ? updateInvoiceFunc : createInvoiceFunc;

        return writeFunc(form.state, original).then(
            ({ data }) => {
                const newInvoice = data.createCMInvoice || data.updateCMInvoice;
                if (!newInvoice) {
                    that.onGqlError('No result received.');
                    return null;
                }
                const editInvoice = cloneDeep(newInvoice);
                const Invoices = parentForm.getField('Invoices');
                if (data.createCMInvoice) {
                    Invoices[Invoices.length] = newInvoice;
                    that.setState({ invoiceIndex: Invoices.length - 1 });
                } else {
                    Invoices[invoiceIndex] = newInvoice;
                }
                parentForm.setField({ Invoices }, true);
                form.setState({ ...editInvoice });
                submitToXeroFunc(form.state).then(
                    result => {
                        if (result && result.data.submitCMInvoiceToXero) {
                            const Invoices = parentForm.getField('Invoices');
                            const updatedInvoice = cloneDeep(result.data.submitCMInvoiceToXero);
                            Object.assign(Invoices[invoiceIndex], updatedInvoice);
                            parentForm.setField({ Invoices }, true);
                            that.setState({
                                loading: false,
                                form: createForm(that, cloneDeep(updatedInvoice)),
                                hasExportedToXero: true,
                                canEdit: false
                            });
                        } else {
                            that.onGqlError('No result received.');
                        }
                    },
                    e => that.onGqlError('Failed to submit invoice to xero.', e)
                );
            },
            e => that.onGqlError('Failed to update invoice.', e)
        );
    }

    renderBillingDetails() {
        const { form: stateForm } = this.state;
        const { form: propsForm } = this.props;
        return (
            <BillTo billingTargetForm={stateForm} billingSourceForm={propsForm} />
        );
    }

    renderBillingDetailsActions = () => {
        const { form } = this.state;
        if (!form) return null;
        const invoice = form.state;
        return <Inline alignment={inlineAlignment.rightAlignSiblings} center>
            <div>
                <Button variant="secondary" onClick={this.closeModal} startIcon={<CloseIcon />}>
                    Cancel
                </Button>
            </div>
            {invoice.ID > 0 && ([null, '', 'NOT EXPORTED', 'DELETED']).includes(invoice.XeroStatus) && (
                <Button variant="decline" startIcon={<DeleteIcon />} onClick={this.onDelete}>
                    Delete
                </Button>
            )}
            <Button onClick={() => this.setState({ tabIndex: 1 })} endIcon={<NextIcon />}>
                Next
            </Button>
        </Inline>;
    };

    renderInvoiceDetails(editingItemsMode) {
        const { form } = this.state;
        if (!form) return null;
        const invoice = form.state;
        const { form: { state: cremation } } = this.props;

        const customerName = invoice.Customer && (invoice.Customer.CustomerName
            ? invoice.Customer.CustomerName
            : joinDefined(
                [
                    invoice.Customer.FirstName,
                    invoice.Customer.MiddleName,
                    invoice.Customer.Surname
                ],
                ' '
            ));
        return (<>
            <Grid container bucket={!!editingItemsMode}>
                <Grid item>
                    {!editingItemsMode && (
                        <div className="biller-details">
                            <div>
                                <ReadonlyField label="Bill to">
                                    <p>
                                        {customerName}
                                    </p>
                                    {invoice.Customer && invoice.Customer.AddressLine1 && (
                                        <div>
                                            {joinDefined(
                                                [invoice.Customer.AddressLine1, invoice.Customer.AddressLine2],
                                                ' '
                                            )}
                                            <br />
                                            {joinDefined(
                                                [
                                                    invoice.Customer.Suburb,
                                                    invoice.Customer.State,
                                                    invoice.Customer.Postcode,
                                                    invoice.Customer.Country !== 'Australia'
                                                        ? invoice.Customer.Country
                                                        : ''
                                                ],
                                                ' '
                                            )}
                                        </div>
                                    )}
                                </ReadonlyField>
                            </div>
                            <div style={{ textAlign: 'end' }}>
                                <ReadonlyField label="Reference">
                                    {!!cremation.Deceased
                                        ? joinDefined(
                                            [
                                                cremation.LegacyKey,
                                                joinDefined(
                                                    [cremation.Deceased.FirstName, cremation.Deceased.Surname],
                                                    ' '
                                                ),
                                                cremation.CremationNumber
                                            ],
                                            ' - '
                                        )
                                        : ''}
                                </ReadonlyField>
                            </div>
                        </div>
                    )}
                    <FlexGrid className="billing-details">
                        <div className="billing-details">
                            <Label>Issue Date</Label>
                            <TextField type="date" name="InvoiceDate" form={form} readOnly={!editingItemsMode} />
                        </div>
                        <div className="billing-details">
                            <Label>Due Date</Label>
                            <TextField type="date" name="InvoiceDueDate" form={form} readOnly={!editingItemsMode} />
                        </div>
                        <div className="billing-details">
                            <Label>Invoice Status</Label>
                            <ReadonlyField>
                                {invoice.XeroStatus || 'NOT EXPORTED'}
                            </ReadonlyField>
                        </div>
                    </FlexGrid>
                </Grid>
            </Grid>
            {this.renderLineItemsTable(editingItemsMode)}
            {!editingItemsMode && (
                <Grid container>
                    <Grid item xs={4}>
                        <ReadonlyField label="Created">
                            {niceDateTimeFromString(form.getField('Created'))} by {joinDefined(
                            [form.getField('CreatedBy.FirstName'), form.getField('CreatedBy.Surname')]
                            , ' ') || 'system'}
                        </ReadonlyField>
                    </Grid>
                    <Grid item xs={4}>
                        <ReadonlyField label="Invoice Number">
                            {form.getField('XeroReference')}
                        </ReadonlyField>
                    </Grid>
                    <Grid item xs={4}>
                        <ReadonlyField label="Sent to Customer">
                            {!form.getField('IsPurchase') ? form.getField('InvoiceSent') ? 'Yes' : 'No' : 'N/A'}
                        </ReadonlyField>
                    </Grid>
                </Grid>
            )}
        </>);
    }

    renderInvoiceDetailsActions = (editingItemsMode = false) => {
        const { form, tabIndex, loading } = this.state;
        if (!form) return null;
        const invoice = form.state;
        const hasItems = invoice.InvoiceItems && invoice.InvoiceItems.filter(e => e.Quantity > 0).length > 0;
        const hasTotal = !!hasItems && invoice.InvoiceItems.reduce((sum, curr) => sum += curr.Price, 0);

        return <Inline alignment={inlineAlignment.rightAlignSiblings} center>
            <Inline>
                <Button variant="secondary" onClick={this.closeModal} startIcon={<CloseIcon />}>
                    Cancel
                </Button>
                <Button onClick={() => this.setState({ tabIndex: tabIndex - 1 })} startIcon={<BackIcon />}>
                    Back
                </Button>
            </Inline>

            {(!!editingItemsMode && this.renderSaveButton()) || (
                <Button
                    variant="urgent"
                    disabled={!!loading || !hasItems}
                    onClick={() => this.submitToXero()}
                    startIcon={loading === 'xero' ? <Spinner size="xs" /> : <UsdCircleIcon />}
                >
                    {loading === 'xero' ? ' Submitting...' : ' Submit to Xero'}
                </Button>
            )}

            {((!!editingItemsMode && !!hasTotal) || !!invoice.ID) && (
                <Button onClick={() => this.setState({ tabIndex: tabIndex + 1 })} endIcon={<NextIcon />}>
                    Next
                </Button>
            )}
        </Inline>;
    };

    onChooseQuote(target) {
        const { quotes } = this.props;
        const { form } = this.state;
        const invoice = form.state;

        setInvoiceFromQuotes(target, { quotes, invoice });
        const updateToggle = 'toggle' + target.value;

        this.setState({
            [updateToggle]: !!target.checked,
            gstPrices: invoice.InvoiceItems.map(x => getPriceIncGst(x))
        });
    }

    renderLineItemsTable(editingItemsMode) {
        const { form } = this.state;
        const invoice = form.state;
        const lineItems = invoice.InvoiceItems;
        const hasItems = lineItems.length > 0;
        const columns = [
            { label: 'Code' },
            { label: 'Description' },
            { label: 'Price', tiny: true },
            { label: 'Quantity', tiny: true },
            { label: 'Account' },
            { label: 'GST', tiny: true },
            { label: 'Amount', tiny: true }
        ];
        if (editingItemsMode) {
            columns.push({ label: '', tiny: true });
            columns.unshift({ label: '', tiny: true });
        }
        const totals = calculateTotals(invoice);
        const tfoot = <tfoot>
            <Row pad className="total-row-desktop">
                <Cell colSpan={editingItemsMode ? 4 : 3}
                      className="empty-cell" />
                <Cell colSpan={2} className="total-cell">
                    TOTALS
                </Cell>
                <Cell className="numeric-cell">
                    <strong>{prettyPrice(totals.gst)}</strong>
                </Cell>
                <Cell className="numeric-cell">
                    <h5>{prettyPrice(totals.totalWithGst)}</h5>
                </Cell>
                {editingItemsMode && <Cell className="empty-cell" />}
            </Row>

            {/*Mobile version of total*/}
            <Row pad className="total-row-mobile">
                <Cell colSpan={1} dataLabel="Invoice GST">
                    <strong>{prettyPrice(totals.gst)}</strong>
                </Cell>
                <Cell colSpan={1} dataLabel="Invoice Total">
                    <h5>{prettyPrice(totals.totalWithGst)}</h5>
                </Cell>
            </Row>
            {/*End Mobile version of total*/}
        </tfoot>;

        const { quotes } = this.props;
        const quoteOptions = !!quotes && quotes.map(e => ({ label: `Quote #${e.quoteID}`, value: '' + e.quoteID }));

        return (
            <Fragment>
                <Grid container bucket={editingItemsMode}>
                    <Grid item>
                        {editingItemsMode && quoteOptions && !!quoteOptions.length ? (
                            <Inline center>
                                <Label>Source Line Items from: </Label>
                                {quoteOptions.map(quote => (
                                    <Checkbox
                                        key={'quote' + quote.value}
                                        checked={!!this.state['toggle' + quote.value]}
                                        label={quote.label}
                                        value={'' + quote.value}
                                        onChange={e => this.onChooseQuote(e.target)}
                                    />
                                ))}
                            </Inline>
                        ) : (
                            <Inline center style={{ padding: '0.725rem 0' }}>
                                <Label>Line Items: </Label>
                            </Inline>
                        )}

                        {hasItems && (
                            <DragDropContext onDragEnd={this.onDragEnd}>
                                <Table columns={columns} tfoot={tfoot} tbody={
                                    <Droppable id="droppable">
                                        {(provided) => (
                                            <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                            {lineItems.map((item, index) =>
                                                this.renderInvoiceItem(item, index, editingItemsMode)
                                            )}
                                            {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                } />
                            </DragDropContext>
                        )}
                        {editingItemsMode ? (
                            <Button variant="tertiary" onClick={() => this.onCreateNewInvoiceItem()}>
                                + Add Line Item
                            </Button>
                        ) : !hasItems && (
                            <Inline center>
                                <h5>There are no line items.</h5>
                            </Inline>
                        )}
                    </Grid>
                </Grid>
            </Fragment>
        );
    }

    renderSaveButton() {
        const { loading } = this.state;
        return (
            <Button
                variant="confirmation"
                disabled={!!loading || !this.invoiceIsModified()}
                onClick={this.onSubmit}
                startIcon={loading === 'save' ? <Spinner size="xs" /> : <SaveIcon />}
            >
                {loading === 'save' ? ' Saving...' : this.invoiceIsModified() ? ' Save as draft' : ' Saved'}
            </Button>
        );
    }

    isPurchaseInvoice() {
        return false;
    }

    onCreateNewInvoiceItem(item = {}, idx = null) {
        const { form } = this.state;
        const invoice = form.state;
        const InvoiceItems = invoice.InvoiceItems;
        const sIdx = (null === idx) ? InvoiceItems.length : idx;
        InvoiceItems.splice(sIdx, null === idx ? 0 : 1, {
            Price: item.Price ? item.Price : 0,
            Quantity: 1,
            Title: item.Title || '',
            HasGST: isNullOrUndefined(item.HasGST) ? true : !!item.HasGST, // assume GST applies unless product says no
            ItemCode: item.ItemCode || '',
            XeroAccount: item.XeroAccount || '',
            ProductID: item.ProductID || '0',
            VariationID: item.VariationID || '0',
            Sort: null === idx ? sIdx : idx,
            sortID: generateTempID()
        });
        form.setField({ InvoiceItems });
        this.setState({
            gstPrices: InvoiceItems.map(x => getPriceIncGst(x)),
            editingItemIndex: InvoiceItems.length - 1
        });
    }

    renderInvoiceItem(item, index, canEdit) {
        const { form } = this.state;
        const { gstPrices } = this.state;
        const isBill = this.isPurchaseInvoice();
        let qty = parseInt(item.Quantity);
        qty = !isNaN(qty) ? qty : 0;
        const priceInclGST = round(gstPrices[index], 2);
        const totalWithGST = round(priceInclGST * qty);
        const gstValue = item.HasGST ? totalWithGST / ((GST + 1) * 10) : 0;
        const itemCode = form.getField(`InvoiceItems[${index}].ItemCode`);
        const key = item.ID || item.sortID || 'new-' + index;
        return (
            <Draggable key={key} id={key} isDragDisabled={!canEdit} index={index}>
                {(provided, snapshot) => (
                    <Row pad ref={provided.innerRef} draggableProps={provided.draggableProps} snapshot={snapshot}>
                        {!!canEdit && (
                            <Cell tiny>
                                <span className="drag-handle" title="Drag" {...provided.dragHandleProps}>
                                    <Icon color="primary">drag_indicator</Icon>
                                </span>
                            </Cell>
                        )}
                        <Cell dataLabel="Code">
                            {itemCode || (canEdit ? <LineItemAutoComplete
                                label=""
                                isPurchase={isBill}
                                variations
                                disabled={!canEdit}
                                onSelect={item => this.onCreateNewInvoiceItem(item, index)}
                            /> : '-')}
                        </Cell>
                        <Cell dataLabel="Description">
                            {canEdit ? (
                                <TextField required name={`InvoiceItems[${index}].Title`} form={form}
                                           placeholder="Describe the item..." />
                            ) : (
                                form.getField(`InvoiceItems[${index}].Title`)
                            )}
                        </Cell>
                        <Cell dataLabel="Price" className="numeric-cell">
                            {canEdit ? (
                                <TextField
                                    style={{ width: 120 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    type="number"
                                    required
                                    value={priceInclGST || '0'}
                                    onChange={e => this.onChangeTotalPrice(index, e.target.value)}
                                />
                            ) : (
                                prettyPrice(priceInclGST || '0')
                            )}
                        </Cell>
                        <Cell dataLabel="Qty" className="numeric-cell">
                            {canEdit ? (
                                <TextField
                                    style={{ width: 60 }}
                                    type="number"
                                    required
                                    onChange={e => this.onChangeQuantity(index, e.target.value)}
                                    name={`InvoiceItems[${index}].Quantity`}
                                    form={form}
                                    value={qty || '0'}
                                />
                            ) : (
                                form.getField(`InvoiceItems[${index}].Quantity`)
                            )}
                        </Cell>
                        <Cell dataLabel="Account" className="w-180">
                            {canEdit ? (
                                <Query query={queryReadAccounts}>
                                    {({ data }) => {
                                        let options = [];
                                        const optStr = data && data.readOneSiteConfig && data.readOneSiteConfig.XeroAccountsCM;
                                        if (optStr) {
                                            const { sales, costs } = JSON.parse(optStr) || {};
                                            if (!form.getField('IsPurchase')) {
                                                Object.keys(sales).forEach(key => {
                                                    options.push({ value: key, label: sales[key] });
                                                });
                                            } else {
                                                Object.keys(costs).forEach(key => {
                                                    options.push({ value: key, label: costs[key] });
                                                });
                                            }
                                        }
                                        const value = '' + (form.getField(`InvoiceItems[${index}].XeroAccount`) || '');
                                        if (!options.length) {
                                            // no xero accounts, allow manual entry
                                            return (
                                                <TextField
                                                    placeholder={!form.getField('IsPurchase') ? 'e.g. 200' : 'e.g. 300'}
                                                    form={form} name={`InvoiceItems[${index}].XeroAccount`}
                                                />
                                            );
                                        }
                                        // use xero accounts, prepend any custom/missing entry
                                        if (value && !options.find(opt => opt.value === value)) {
                                            options.unshift({ label: `[${value}]`, value });
                                        }
                                        return (
                                            <Select
                                                form={form}
                                                name={`InvoiceItems[${index}].XeroAccount`}
                                                options={options}
                                            />
                                        );
                                    }}
                                </Query>
                            ) : form.getField(`InvoiceItems[${index}].XeroAccount`)}
                        </Cell>
                        <Cell dataLabel="GST" className="numeric-cell">
                            {canEdit && !itemCode && (
                                <Checkbox
                                    name={`InvoiceItems[${index}].HasGST`}
                                    form={form}
                                    onChange={e => this.onChangeHasGST(index, e.target.checked)}
                                />
                            )}
                            {prettyPrice(gstValue)}
                        </Cell>
                        <Cell dataLabel="Total" className="numeric-cell">{prettyPrice(totalWithGST)}</Cell>
                        {!!canEdit && (
                            <Cell>
                                <IconButton
                                    color="primary"
                                    style={{ width: 18, height: 18 }}
                                    title="Delete"
                                    onClick={() => this.onDeleteLineItem(index)} size="large">
                                    <DeleteIcon style={{ width: 14, height: 14 }} />
                                </IconButton>
                            </Cell>
                        )}
                    </Row>
                )}
            </Draggable>
        );
    }

    onChangeHasGST(lineItemIndex, checked) {
        const { form } = this.state;
        const invoice = form.state;

        const lineItem = invoice.InvoiceItems[lineItemIndex];
        const price = lineItem.Price;
        recalculatePrice(lineItem, checked ? price : price * (GST + 1));
    }

    onDragEnd = ({ operation }) => {
        const { source, target } = operation;
        // dropped outside the list
        if (!target) return;
        const reorder = (list, startIndex, endIndex) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result.map((item, idx) => ({ ...item, Sort: idx }));
        };
        const oldIndex = source.sortable?.initialIndex ?? source.index;
        const newIndex = source.sortable?.index ?? target.index;
        const { form } = this.state;
        const invoice = form.state;
        invoice.InvoiceItems = reorder(invoice.InvoiceItems, oldIndex, newIndex);
        this.setState({
            gstPrices: invoice.InvoiceItems.map(x => getPriceIncGst(x))
        });
    };

    onDeleteLineItem(index) {
        const { form } = this.state;
        const invoice = form.state;
        const newItems = invoice.InvoiceItems.toSpliced(index, 1);
        form.setField({ InvoiceItems: newItems });
        this.setState({ gstPrices: newItems.map(x => getPriceIncGst(x)) });
    }

    onChangeTotalPrice(lineItemIndex, stringValue) {
        const { form } = this.state;
        const invoice = form.state;

        const value = typeof stringValue === 'number' ? stringValue : parseFloat(stringValue);
        if (isNaN(value)) return 0;

        const { gstPrices } = this.state;
        const lineItem = invoice.InvoiceItems[lineItemIndex];

        gstPrices[lineItemIndex] = value;
        recalculatePrice(lineItem, value);

        this.setState({ gstPrices });
    }

    onChangeQuantity(index, stringValue) {
        const { form } = this.state;
        const invoice = form.state;
        const value = typeof stringValue === 'number' ? stringValue : parseInt(stringValue, 10);
        if (isNaN(value) || value < 0) return 0;

        const lineItem = invoice.InvoiceItems[index];
        lineItem.Quantity = value;

        this.forceUpdate();
        this.setState({ gstPrices: invoice.InvoiceItems.map(x => getPriceIncGst(x)) });
    }

    renderPreview() {
        const { form } = this.state;

        if (!form) return null;

        const invoice = form.state;
        const createdBy = invoice.CreatedBy;
        let userName = '';
        if (createdBy && !!parseInt(createdBy.ID)) {
            userName = joinDefined(
                [joinDefined([createdBy.FirstName, createdBy.Surname], ' '), niceTimeFromString(invoice.Created)],
                ', '
            );
        }
        return (
            <Grid container>
                {this.renderPdfView()}
                <Grid item xs={12} sm={4}>
                    <Grid container>
                        <Grid item>
                            <h4>Invoice Details</h4>
                        </Grid>
                        <Grid item>
                            <TextField label="Invoice Status" name="XeroStatus" form={form} readOnly />
                        </Grid>
                        <Grid item>
                            <TextField label="Issue Date" type="date" name="InvoiceDate" form={form} readOnly />
                        </Grid>
                        <Grid item>
                            <TextField label="Created By" value={userName} readOnly />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    renderPreviewActions() {
        const { tabIndex } = this.state;
        return <Inline alignment={inlineAlignment.rightAlignSiblings} center>
            <Inline>
                <Button variant="secondary" onClick={this.closeModal} startIcon={<CloseIcon />}>
                    Close
                </Button>
                {tabIndex > 0 && (
                    <Button onClick={() => this.setState({ tabIndex: tabIndex - 1 })} startIcon={<BackIcon />}>
                        Back
                    </Button>
                )}
            </Inline>
            <Button onClick={() => this.setState({ tabIndex: tabIndex + 1 })} endIcon={<NextIcon />}>
                Next
            </Button>
        </Inline>;
    }

    renderPdfView() {
        const { form } = this.state;
        const pdfLink = form.getField('XeroID')
            ? false
            : 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
        if (pdfLink) return null;

        return (
            <Grid item xs={12} sm={8}>
                <Grid container>
                    <Grid item>
                        <Query query={queryReadInvoicePDF} variables={{ ID: form.getField('ID') }}>
                            {({ data, loading }) => {
                                if (loading) {
                                    return (
                                        <Fragment>
                                            <Spinner /> ...fetching invoice from Xero
                                        </Fragment>
                                    );
                                }
                                let { readCMInvoicePDF } = data || {};

                                if (readCMInvoicePDF) {
                                    readCMInvoicePDF = 'data:application/pdf;base64,' + readCMInvoicePDF;
                                    return (
                                        <iframe
                                            src={readCMInvoicePDF}
                                            style={{ width: '100%', height: 510 }}
                                            title="invoice-pdf"
                                        />
                                    );
                                }

                                return 'PDF not found.';
                            }}
                        </Query>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    renderNoteHistory() {
        return (
            <Grid bucket>
                <Grid item xs={12} md={7}>
                    {this.renderLeftNotes()}
                </Grid>
                <Grid item xs={12} md={5}>
                    {this.renderRightNotes()}
                </Grid>
            </Grid>
        );
    }

    renderNoteHistoryActions() {
        const { tabIndex, form, loading } = this.state;
        const buttonDisable = !!loading || !form.getField('Notes');
        return <Inline alignment={inlineAlignment.rightAlignSiblings} center>
            <Inline>
                <Button variant="secondary" onClick={this.closeModal} startIcon={<CloseIcon />}>
                    Close
                </Button>
                <Button onClick={() => this.setState({ tabIndex: tabIndex - 1 })} startIcon={<BackIcon />}>
                    Back
                </Button>
            </Inline>
            <Button variant="confirmation" disabled={buttonDisable} onClick={this.onAddNote}
                    startIcon={!!loading ? <Spinner size="xs" /> : <SaveIcon />}>
                Add Note
            </Button>
        </Inline>;
    }

    renderLeftNotes() {
        const { form } = this.state;
        if (!form) return null;
        return (
            <>
                <Label>Add a note</Label>
                <TextField name="Notes" form={form} placeholder="Type a note here..." multiline rows={8} />
            </>
        );
    }

    renderRightNotes() {
        const { notes } = this.state;
        return (
            <>
                <Label>Notes History</Label>
                <NotesReadOnly name="NotesValue" value={notes} />
            </>
        );
    }

    onGqlError(action, error) {
        const { setSnackbarMessage } = this.props;
        setSnackbarMessage(action + (error ? ' - ' + error.message : ''));
        this.setState({ loading: false });
    }

    defaultCustomer() {
        return {
            AddressLine1: null,
            AddressLine2: null,
            Suburb: null,
            State: null,
            Postcode: null,
            BillingId: null,
            Country: null,
            Mobile: null,
            Phone: null,
            FirstName: null,
            MiddleName: null,
            Surname: null,
            CustomerName: null,
            BillingSource: null,
            RelationToDeceased: null,
            Email: null
        };
    }
}

const getPriceIncGst = invoiceLineItem => {
    const price = parseFloat(invoiceLineItem.Price);
    return invoiceLineItem.HasGST ? price * (GST + 1) : price;
};

const recalculatePrice = (invoiceLineItem, newGstPrice) => {
    invoiceLineItem.Price = invoiceLineItem.HasGST ? newGstPrice / (GST + 1) : newGstPrice;
};

export const calculateTotals = invoice => {
    let total = 0,
        gst = 0,
        totalWithGst = 0;

    for (let x = 0; x < invoice.InvoiceItems.length; x++) {
        const item = invoice.InvoiceItems[x];
        if (item.Quantity > 0) {
            const itemTotal = item.Price * item.Quantity;

            total += itemTotal;

            const itemTotalWithGst = item.HasGST ? round(itemTotal * (GST + 1), 2) : itemTotal;
            totalWithGst += itemTotalWithGst;

            gst += item.HasGST ? itemTotalWithGst / ((GST + 1) * 10) : 0;
        }
    }

    return { total, gst, totalWithGst };
};

const setInvoiceFromQuotes = ({ value, checked }, { quotes, invoice }) => {
    const newItems = { list: [] };
    const oldList = invoice.InvoiceItems.filter(e => !!e); // clone
    if (value !== false) {
        const quote = quotes.find(e => e.quoteID === value);
        if (quote) {
            //strip the quote items
            newItems.list = oldList.filter(e => e.QuoteID !== value);
            // add back in if wanted
            if (checked) newItems.list = [].concat(quote.items, newItems.list);
        }
    }
    invoice.InvoiceItems = newItems.list.map((e, i) => ({
        ...e,
        Sort: i,
        sortID: e.sortID || generateTempID()
    }));
};


const generateTempID = () => {
    return (
        Date.now().toString(36) +
        Math.random()
            .toString(36)
            .substring(2)
    );
};

const queryReadInvoicePDF = gql`
    query ReadInvoicePDF ($ID: ID!) {
        readCMInvoicePDF(ID: $ID)
    }
`;

const queryReadAccounts = gql`
    query ReadSiteConfig {
        readOneSiteConfig {
            ID
            XeroAccountsCM
        }
    }
`;

export default compose(withSnackbarMessage)(InvoiceModal);
export const InvoiceModalExtendable = InvoiceModal; // for SupplierBillingModal
